<template>
  <BaseCardSection
      title="Parametry zálohové faktury"
      :is-collapsible="isCollapsible"
      :default-open="defaultOpen"
      @validation="handleValidation"
  >
    <div class="row my-3 align-items-center">
      <div class="col-md-4">
        <div class="form-check form-switch">
          <label class="form-check-label">
            Automaticky vytvořit zálohovou fakturu
          </label>
          <input
              class="form-check-input"
              type="checkbox"
              :checked="proformaCreate"
              @change="$emit('update:proformaCreate', $event.target.checked)"
              v-tooltip="'Automaticky vytvoří zálohovou fakturu ve fakturoidu po uložení objednávky. V případě nastavení platby v hotovosti se faktura rovnou označí jako uhrazená a fakturoid odešle daňový doklad na email uvedený u zákazníka.'"
              switch>
        </div>
      </div>
      <!-- Splatnost -->
      <div class="col-md-3">
        <label class="form-label">Splatnost (dny):</label>
        <input
            type="number"
            name="due"
            class="form-control"
            :value="due"
            @input="$emit('update:due', parseInt($event.target.value))"
            v-validate="{
              rules: { required: true, min: 0, max: 60},
              message: { required: 'Splatnost je povinná', min: 'Splatnost musí být minimálně 1 den', max: 'Splatnost musí být maximálně 60 dní'},
              options: { showValidState: false, validateOnMount: true }
            }"
        >
      </div>

      <!-- Typ platby -->
      <div class="col-md-5">
        <label class="form-label">Typ platby:</label>
        <select
            class="form-select"
            name="payment_method"
            :value="payment_method"
            @change="$emit('update:payment_method', $event.target.value)"
            required
        >
          <option v-for="method in paymentMethods" :key="method.value" :value="method.value">
            {{ method.label }}
          </option>
        </select>
      </div>
    </div>

    <div class="row my-3 align-items-center">
      <div class="col-md-4"></div>

      <!-- Provize procentem -->
      <div class="col-md-3">
        <label class="form-label">Provize (%):</label>
        <input
            type="number"
            name="commission_rate"
            class="form-control"
            :value="commissionRate"
            @input="$emit('update:commissionRate', parseFloat($event.target.value) || 0)"
            min="0"
            step="0.01"
            disabled
        >
      </div>

      <!-- Provize Kč -->
      <div class="col-md-5">
        <label class="form-label">Provize (Kč):</label>
        <input
            type="number"
            name="commission_price"
            class="form-control"
            :value="commissionPrice"
            @input="$emit('update:commissionPrice', parseFloat($event.target.value) || 0)"
            min="0"
            step="0.01"
            disabled
        >
      </div>
    </div>

    <div class="row my-3">
      <!-- Individuální text -->
      <div class="col-md-12">
        <label class="form-label">Individuální text na zálohovou fakturu:</label>
        <textarea
            class="form-control"
            name="line_text"
            rows="3"
            :value="internalLineText"
            @input="$emit('update:line_text', $event.target.value)"
            placeholder="Zadej text pro položku na zálohové faktuře..."
            v-validate="{
              rules: { required: true},
              message: { required: 'Text na fakturu je povinný'},
              options: { showValidState: false, validateOnMount: true }
            }">
        </textarea>
      </div>
    </div>
  </BaseCardSection>
</template>

<script setup>
import {defineProps, defineEmits, ref, onMounted} from 'vue';
import BaseCardSection from "@/components/base/BaseCardSection.vue";

const internalLineText = ref();
const fieldStatus = ref({});

const props = defineProps({
  orderNumber: {
    type: String,
    required: true
  },
  calculationName: {
    type: String,
    required: true
  },
  due: {
    type: Number,
    default: 14
  },
  payment_method: {
    type: String,
    default: 'bank'
  },
  line_text: {
    type: String,
    default: ''
  },
  commissionRate: {
    type: Number,
    default: 0
  },
  commissionPrice: {
    type: Number,
    default: 0
  },
  isCollapsible: {
    type: Boolean,
    default: false
  },
  proformaCreate: {
    type: Boolean,
    default: true
  },
  defaultOpen: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits([
  'update:due',
  'update:payment_method',
  'update:line_text',
  'update:isSectionValid',
  'update:proformaCreate',
  'update:commissionRate',
  'update:commissionPrice'
]);

const paymentMethods = ref([
  {value: 'bank', label: 'Bankovní převod'},
  {value: 'cash', label: 'Hotovost - okamžitá platba'},
]);

/**
 * Zpracovává eventy z v-validate direktivy.
 * Ukládá stav jednotlivých polí a emituje celkovou validitu sekce.
 */
const handleValidation = (event) => {
  const {fieldName, isValid} = event.detail;

  // Uložíme stav konkrétního pole
  fieldStatus.value[fieldName] = isValid;

  // Sekce je validní pouze pokud jsou VŠECHNA registrovaná pole true
  const isSectionValid = Object.values(fieldStatus.value).every(status => status === true);

  emit('update:isSectionValid', isSectionValid);
};

onMounted(() => {
  internalLineText.value = `Dle objednávky Vám fakturujeme zálohu na zakázku - ${props.calculationName}`;
  emit('update:line_text', internalLineText.value);
})
</script>

<style scoped>
/* Styly zůstávají stejné */
textarea {
  resize: vertical;
}
</style>